import { supabase } from './supabase';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
  booking?: {
    id: string;
    business_id: string;
    booking_number: number;
    title: string;
    status: string;
    created_at: string;
    end_date: string;
    end_time: string;
    completed_at: string;
  };
}

export async function getPayrollEntries(businessId: string, startDate?: string, endDate?: string) {
  if (!businessId) {
    throw new Error('Business ID is required');
  }

  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Query for flat rate entries
    const { data: flatRateData, error: flatRateError } = await supabase
      .from('payroll_page')
      .select(`
        *,
        booking:bookings(
          id,
          business_id,
          booking_number,
          title,
          status,
          created_at,
          end_date,
          end_time,
          completed_at
        ),
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        ),
        item:flat_rate_items(
          name,
          description
        )
      `)
      .eq('business_id', businessId)
      .eq('entry_type', 'flat_rate');

    if (flatRateError) {
      throw flatRateError;
    }
    
    // Query for timeclock entries
    const { data: timeclockData, error: timeclockError } = await supabase
      .from('payroll_page')
      .select(`
        *,
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        )
      `)
      .eq('business_id', businessId)
      .eq('entry_type', 'timeclock');
      
    if (timeclockError) {
      throw timeclockError;
    }

    // Query for commission entries
    const { data: commissionData, error: commissionError } = await supabase
      .from('payroll_page')
      .select(`
        *,
        booking:bookings(
          id,
          business_id,
          booking_number,
          title,
          status,
          created_at,
          end_date,
          end_time,
          completed_at
        ),
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        )
      `)
      .eq('business_id', businessId)
      .eq('entry_type', 'commission');
      
    if (commissionError) {
      throw commissionError;
    }

    // Convert flat rate entries to PayrollEntry format
    const flatRateEntries = flatRateData?.map(entry => {
      const mappedEntry = {
        id: entry.id,
        resource_id: entry.resource_id,
        resource_first_name: entry.resource?.first_name,
        resource_last_name: entry.resource?.last_name,
        resource_email: entry.resource?.email,
        compensation_type: entry.resource?.compensation_type,
        flat_rate_amount: entry.resource?.flat_rate_amount,
        hourly_rate: entry.resource?.hourly_rate,
        booking_id: entry.booking_id,
        booking_number: entry.booking?.booking_number,
        booking_title: entry.booking?.title,
        start_date: entry.created_at,
        end_date: entry.completed_at || entry.created_at,
        entry_type: 'flat_rate' as const,
        hours: entry.hours || (entry.total_minutes ? entry.total_minutes / 60 : 0),
        earnings: entry.earnings || 0,
        item_name: entry.item?.name,
        item_description: entry.item?.description,
        created_at: entry.created_at,
        completed_at: entry.completed_at,
        booking: entry.booking,
        // Optional fields that might be needed for interface compatibility
        clock_in: undefined,
        clock_out: undefined,
        break_start: undefined,
        break_end: undefined,
        total_minutes: undefined,
        notes: entry.notes,
        status: entry.status,
        process_status: entry.process_status
      };

      return mappedEntry;
    }) || [];
    
    // Convert timeclock entries to PayrollEntry format
    const timeclockEntries = timeclockData?.map(entry => {
      // Calculate hours and earnings if not already set
      const hours = entry.hours || (entry.total_minutes ? entry.total_minutes / 60 : 0);
      const rate = entry.rate_amount || entry.resource?.hourly_rate || 0;
      const earnings = entry.total_earnings || (hours * rate);
      
      const mappedEntry = {
        id: entry.id,
        resource_id: entry.resource_id,
        resource_first_name: entry.resource?.first_name,
        resource_last_name: entry.resource?.last_name,
        resource_email: entry.resource?.email,
        compensation_type: entry.compensation_type || entry.resource?.compensation_type || 'hourly',
        flat_rate_amount: null,
        hourly_rate: entry.rate_amount || entry.resource?.hourly_rate,
        booking_id: null,
        booking_number: null,
        booking_title: null,
        start_date: entry.clock_in,
        end_date: entry.clock_out || entry.created_at,
        entry_type: 'timeclock' as const,
        hours: hours,
        earnings: earnings,
        item_name: null,
        item_description: null,
        created_at: entry.created_at,
        completed_at: entry.clock_out,
        // Timeclock specific fields
        clock_in: entry.clock_in,
        clock_out: entry.clock_out,
        break_start: entry.break_start,
        break_end: entry.break_end,
        total_minutes: entry.total_minutes,
        notes: entry.notes,
        status: entry.status,
        process_status: entry.process_status
      };
      
      return mappedEntry;
    }) || [];
    
    // Convert commission entries to PayrollEntry format
    const commissionEntries = commissionData?.map(entry => {
      const mappedEntry = {
        id: entry.id,
        resource_id: entry.resource_id,
        resource_first_name: entry.resource?.first_name,
        resource_last_name: entry.resource?.last_name,
        resource_email: entry.resource?.email,
        compensation_type: entry.resource?.compensation_type,
        flat_rate_amount: entry.resource?.flat_rate_amount,
        hourly_rate: entry.resource?.hourly_rate,
        booking_id: entry.booking_id,
        booking_number: entry.booking?.booking_number,
        booking_title: entry.booking?.title,
        start_date: entry.created_at,
        end_date: entry.completed_at || entry.created_at,
        entry_type: 'commission' as const,
        hours: 0, // Commissions don't have hours
        earnings: entry.total_earnings || 0,
        item_name: entry.item_name,
        item_description: entry.item_description,
        created_at: entry.created_at,
        completed_at: entry.completed_at,
        booking: entry.booking,
        // Optional fields for interface compatibility
        clock_in: undefined,
        clock_out: undefined,
        break_start: undefined,
        break_end: undefined,
        total_minutes: undefined,
        notes: entry.notes,
        status: entry.status,
        process_status: entry.process_status
      };

      return mappedEntry;
    }) || [];
    
    // Combine all types of entries
    const allEntries = [...flatRateEntries, ...timeclockEntries, ...commissionEntries];
    
    console.log(`Payroll Entries: Found ${flatRateEntries.length} flat rate entries, ${timeclockEntries.length} timeclock entries, and ${commissionEntries.length} commission entries`);

    // Filter entries by date
    const filteredEntries = allEntries.filter(entry => {
      // For timeclock entries, use clock_in date
      // For flat rate entries, use completed_at or end_date
      const entryDate = entry.entry_type === 'timeclock' && entry.clock_in
        ? new Date(entry.clock_in)
        : entry.completed_at
          ? new Date(entry.completed_at)
          : new Date(entry.end_date || entry.created_at);
      
      if (!startDate || !endDate) {
        return true;
      }

      const entryDateStr = entryDate.toISOString().split('T')[0];
      const start = new Date(startDate);
      const end = new Date(endDate);
      const startDateStr = start.toISOString().split('T')[0];
      const endDateStr = end.toISOString().split('T')[0];
      
      return entryDateStr >= startDateStr && entryDateStr <= endDateStr;
    });

    // Sort entries by date (most recent first)
    return filteredEntries.sort((a, b) => {
      const dateA = a.entry_type === 'timeclock' && a.clock_in
        ? new Date(a.clock_in)
        : a.completed_at
          ? new Date(a.completed_at)
          : new Date(a.end_date || a.created_at);
          
      const dateB = b.entry_type === 'timeclock' && b.clock_in
        ? new Date(b.clock_in)
        : b.completed_at
          ? new Date(b.completed_at)
          : new Date(b.end_date || b.created_at);
          
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error getting payroll entries:', error);
    throw error;
  }
}